import React from 'react';
import { useTranslation } from 'react-i18next';
import { useBreakpoint } from 'hooks';
import {
  BannerContainer,
  BannerContent,
  BannerIconContainer,
  BannerText,
} from './OSREditBanner.styles';

interface OSREditBannerProps {
  onEdit?: () => void;
}

// Custom icon as inline SVG
const CustomIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="17" viewBox="0 0 20 17" fill="none">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M2.38542 8.66375C2.52153 8.25541 2.90367 7.97998 3.3341 7.97998H15.4293C15.8417 7.97998 16.2118 8.23316 16.3613 8.61753L19.0835 15.6175C19.3384 16.2731 18.8549 16.98 18.1515 16.98H1.00077C0.31821 16.98 -0.16376 16.3113 0.0520833 15.6638L2.38542 8.66375ZM14.6133 9.97998H4.11334L2.61334 14.98H16.6133L14.6133 9.97998Z"
      fill="white"
    />
    <path
      d="M8.61334 12.2802H10.6133V5.12629L9.53131 5.12611L8.61334 5.02686V12.2802Z"
      fill="white"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M5.57593 0.0315898C6.65981 0.128226 7.68595 0.455376 8.26155 1.17408C8.93464 2.01451 9.27423 3.19215 9.43015 4.24877C9.47858 4.577 9.50759 4.88151 9.52461 5.14366C9.34037 5.13889 9.13492 5.12551 8.91585 5.09818C8.0452 4.9896 7.03722 4.67201 6.24916 3.88403C5.44391 3.07885 5.00054 1.91888 4.7683 0.873209C4.69782 0.555881 4.64916 0.259881 4.61561 0.00296815C4.89959 -0.00433068 5.22848 0.000611499 5.57593 0.0315898ZM9.53676 5.13725C9.5848 5.01603 9.63925 4.88845 9.70076 4.75665C10.0879 3.92717 10.7254 2.99156 11.7466 2.37883C12.7238 1.79252 13.7021 1.78509 14.4735 1.93934C14.6364 1.97191 14.788 2.01142 14.9258 2.05343C14.8376 2.23686 14.7336 2.43849 14.6141 2.64745C14.1439 3.46937 13.5161 4.25774 12.7839 4.62478C11.9931 5.02123 10.9306 5.14566 9.9818 5.14593C9.82625 5.14597 9.67712 5.1427 9.53676 5.13725Z"
      fill="white"
    />
  </svg>
);

const OSREditBanner: React.FC<OSREditBannerProps> = ({ onEdit }) => {
  const [t] = useTranslation();
  const { isMobile } = useBreakpoint();

  const getDDGUrl = () => {
    const env = process.env.REACT_APP_ENV;

    if (env === 'prod') {
      return 'https://seedselector.syngenta.com/';
    }
    // Default to UAT for all other environments (uat, qa, dev, local)
    return 'https://uat.seedsddg.cropwise.com/';
  };

  const buildDDGUrl = () => {
    const baseUrl = getDDGUrl();

    if (typeof window === 'undefined') {
      return baseUrl;
    }

    const url = new URL(baseUrl);
    const params = new URLSearchParams(window.location.search);

    params.forEach((value, key) => {
      if (key.toLowerCase().startsWith('utm_')) {
        url.searchParams.append(key, value);
      }
    });

    return url.toString();
  };

  const handleEditClick = () => {
    if (onEdit) {
      onEdit();
    }
    const ddgUrl = buildDDGUrl();
    window.open(ddgUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <BannerContainer isMobile={isMobile} onClick={handleEditClick} data-banner="osr-edit">
      <BannerContent isMobile={isMobile}>
        <BannerIconContainer>
          <CustomIcon />
        </BannerIconContainer>
        <BannerText>
          {t(
            'Your fields are unique — click here and create your custom recommendation and discover hybrids that fit best.'
          )}
        </BannerText>
      </BannerContent>
    </BannerContainer>
  );
};

// eslint-disable-next-line import/no-default-export
export default OSREditBanner;
